import { cn } from '../../lib/cn'

type CardElevation = 'resting' | 'raised'

interface CardProps {
  children: React.ReactNode
  className?: string
  elevation?: CardElevation
  /** Adds the firelight glow + ember wash for the active/top-pick element only. */
  glow?: boolean
  onClick?: () => void
}

// Elevation is a lightness step plus a soft warm shadow (design-system §4);
// raised chrome also takes the warm border (§2.2) so the tiers read apart.
const elevationStyles: Record<CardElevation, string> = {
  resting: 'bg-surface border-border shadow-warm-sm',
  raised: 'bg-surface-raised border-border-warm shadow-warm',
}

export function Card({ children, className, elevation = 'resting', glow = false, onClick }: CardProps) {
  return (
    <div
      {...(onClick
        ? {
            role: 'button',
            tabIndex: 0,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            },
          }
        : {})}
      onClick={onClick}
      className={cn(
        'rounded-card border p-5',
        elevationStyles[elevation],
        glow && 'border-transparent ring-1 ring-ember-400/50 shadow-glow bg-ember-500/12',
        onClick &&
          'cursor-pointer text-left transition duration-200 ease-out hover:-translate-y-0.5 hover:shadow-warm motion-reduce:hover:translate-y-0 w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember-400 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        className,
      )}
    >
      {children}
    </div>
  )
}
